import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Toaster } from '@/components/ui/sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCreateUserService } from '@/services/backoffice';
import * as EmailValidator from 'email-validator';
import { useMemo, useState, type FC } from 'react';
// As required by shadcn : https://ui.shadcn.com/docs/components/sonner
import { toast } from 'sonner';

interface CreateUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateUserModal: FC<CreateUserModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { createUser, isLoading } = useCreateUserService();
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    locale: 'en',
    type: 'client' as 'ditto' | 'client',
  });

  const [createWorkspace, setCreateWorkspace] = useState(false);
  const [workspaceData, setWorkspaceData] = useState({
    name: '',
    uname: '',
  });

  const formValidation = useMemo(() => {
    const isEmailValid =
      newUser.email.trim() !== '' && EmailValidator.validate(newUser.email);
    const isFirstNameValid = newUser.firstName.trim() !== '';
    const isLastNameValid = newUser.lastName.trim() !== '';
    const isLocaleValid = newUser.locale !== '';

    // Workspace validation (only if createWorkspace is enabled)
    const isWorkspaceNameValid =
      !createWorkspace || workspaceData.name.trim() !== '';
    const isWorkspaceUnameValid =
      !createWorkspace ||
      (workspaceData.uname.trim() !== '' &&
        /^[a-z0-9-]+$/.test(workspaceData.uname));

    const isFormValid =
      isEmailValid &&
      isFirstNameValid &&
      isLastNameValid &&
      isLocaleValid &&
      isWorkspaceNameValid &&
      isWorkspaceUnameValid;

    const invalidFields = [];
    if (!isEmailValid) {
      if (newUser.email.trim() === '') {
        invalidFields.push('Email is required');
      } else {
        invalidFields.push('Email format is invalid');
      }
    }
    if (!isFirstNameValid) invalidFields.push('First name is required');
    if (!isLastNameValid) invalidFields.push('Last name is required');
    if (!isLocaleValid) invalidFields.push('Locale is required');

    if (createWorkspace) {
      if (!isWorkspaceNameValid)
        invalidFields.push('Workspace name is required');
      if (!isWorkspaceUnameValid) {
        if (workspaceData.uname.trim() === '') {
          invalidFields.push('Workspace URL is required');
        } else {
          invalidFields.push('Workspace URL format is invalid');
        }
      }
    }

    return {
      isValid: isFormValid,
      errors: invalidFields,
    };
  }, [newUser, createWorkspace, workspaceData]);

  const handleCreateUser = () => {
    const userData = {
      ...newUser,
      ...(createWorkspace && {
        createWorkspace: {
          name: workspaceData.name,
          uname: workspaceData.uname,
          features: [
            { feature: 'regulatoryWatch', status: 'ACTIVATED' },
            { feature: 'chat', status: 'ACTIVATED' },
          ],
        },
      }),
    };

    createUser(userData, {
      onSuccess: () => {
        toast.success('User created successfully');
        onOpenChange(false);
        setNewUser({
          firstName: '',
          lastName: '',
          email: '',
          locale: 'en',
          type: 'client',
        });
        setCreateWorkspace(false);
        setWorkspaceData({
          name: '',
          uname: '',
        });
      },
      onError: (error: any) => {
        // Try different possible error message locations
        const errorMessage =
          error?.message ||
          error?.response?.body?.message ||
          error?.response?.data?.message ||
          'Failed to create user';

        toast.error(errorMessage);
      },
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={newUser.email}
                onChange={e =>
                  setNewUser({ ...newUser, email: e.target.value })
                }
                placeholder="Enter email address"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={newUser.firstName}
                onChange={e =>
                  setNewUser({ ...newUser, firstName: e.target.value })
                }
                placeholder="Enter first name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={newUser.lastName}
                onChange={e =>
                  setNewUser({ ...newUser, lastName: e.target.value })
                }
                placeholder="Enter last name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="locale">Locale</Label>
              <Select
                value={newUser.locale}
                onValueChange={(value: string) =>
                  setNewUser({ ...newUser, locale: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select locale" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English (en)</SelectItem>
                  <SelectItem value="fr">French (fr)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">User Type</Label>
              <Select
                value={newUser.type}
                onValueChange={(value: 'ditto' | 'client') =>
                  setNewUser({ ...newUser, type: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select user type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="ditto">Ditto</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Workspace Creation Section */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="createWorkspace"
                  checked={createWorkspace}
                  onCheckedChange={checked =>
                    setCreateWorkspace(checked as boolean)
                  }
                />
                <Label htmlFor="createWorkspace">
                  Create workspace for this user
                </Label>
              </div>

              {createWorkspace && (
                <div className="space-y-4 pl-6 border-l-2 border-muted">
                  <div className="space-y-2">
                    <Label htmlFor="workspace-name">Workspace Name</Label>
                    <Input
                      id="workspace-name"
                      value={workspaceData.name}
                      onChange={e =>
                        setWorkspaceData({
                          ...workspaceData,
                          name: e.target.value,
                        })
                      }
                      placeholder="Enter workspace name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="workspace-uname">Workspace URL</Label>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-muted-foreground">
                        /workspace/
                      </span>
                      <Input
                        id="workspace-uname"
                        value={workspaceData.uname}
                        onChange={e => {
                          // Auto-convert to lowercase and replace invalid characters
                          const cleanUname = e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9-]/g, '');
                          setWorkspaceData({
                            ...workspaceData,
                            uname: cleanUname,
                          });
                        }}
                        placeholder="workspace-url"
                        className="font-mono text-sm"
                        required
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Only lowercase letters, numbers, and hyphens allowed
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      onClick={handleCreateUser}
                      disabled={!formValidation.isValid || isLoading}
                    >
                      Create User
                    </Button>
                  </div>
                </TooltipTrigger>
                {!formValidation.isValid && (
                  <TooltipContent>
                    <div className="space-y-1">
                      {formValidation.errors.map((error, index) => (
                        <div key={index} className="text-xs">
                          • {error}
                        </div>
                      ))}
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Toaster />
    </>
  );
};
